import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchAccounts, createAccount, updateAccount, deleteAccount,
  testAccountConnection, setDataFeedAccount,
  getAngelOneLoginUrl, getAngelOneDefaultKey, getKiteLoginUrl,
  fetchStrategyConfigs, createStrategyConfig, updateStrategyConfig, deleteStrategyConfig,
} from '../api';

function StrategyInstance({ instance, accounts, meta, onSave, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    start_time: instance.start_time || '09:20',
    exit_time: instance.exit_time || '14:40',
    strike_mode: instance.strike_mode || 'ATM',
    itm_points: instance.itm_points || 0,
    lots: instance.lots || 1,
    account_id: instance.account_id || '',
    is_active: instance.is_active !== false,
  });

  const handleSave = () => { onSave(instance.id, form); setEditing(false); };

  if (!editing) {
    return (
      <div className="instance-row">
        <span className="instance-account">{instance.account_name || 'Paper'}</span>
        <span className="instance-detail">Start: {instance.start_time || '09:20'}</span>
        <span className="instance-detail">Exit: {instance.exit_time || '14:40'}</span>
        <span className="instance-detail">{instance.strike_mode || 'ATM'}{instance.strike_mode === 'ITM' ? ` ${instance.itm_points}pts` : ''}</span>
        <span className="instance-detail">Lots: {instance.lots || 1}</span>
        <span className={`instance-status ${instance.is_active !== false ? 'active' : 'inactive'}`}>
          {instance.is_active !== false ? '● Active' : '○ Inactive'}
        </span>
        <div className="instance-actions">
          <button className="btn-sm" onClick={() => setEditing(true)}>Edit</button>
          <button className="btn-sm btn-danger" onClick={() => onDelete(instance.id)}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div className="instance-row instance-editing">
      <div className="instance-form-grid">
        <div className="form-field">
          <label>Account</label>
          <select value={form.account_id} onChange={e => setForm({...form, account_id: e.target.value ? parseInt(e.target.value) : ''})}>
            <option value="">Paper Trading</option>
            {accounts.map(acc => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
          </select>
        </div>
        <div className="form-field">
          <label>Start Time</label>
          <input type="time" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})} />
        </div>
        <div className="form-field">
          <label>Exit Time</label>
          <input type="time" value={form.exit_time} onChange={e => setForm({...form, exit_time: e.target.value})} />
        </div>
        <div className="form-field">
          <label>Strike Mode</label>
          <select value={form.strike_mode} onChange={e => setForm({...form, strike_mode: e.target.value})}>
            <option value="ATM">ATM</option>
            <option value="ITM">ITM</option>
          </select>
        </div>
        {form.strike_mode === 'ITM' && (
          <div className="form-field">
            <label>ITM Points</label>
            <input type="number" value={form.itm_points} onChange={e => setForm({...form, itm_points: parseInt(e.target.value) || 0})} min="0" step="50" />
          </div>
        )}
        <div className="form-field">
          <label>Lots</label>
          <input type="number" value={form.lots} onChange={e => setForm({...form, lots: parseInt(e.target.value) || 1})} min="1" />
        </div>
        <div className="form-field">
          <label><input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} /> Active</label>
        </div>
      </div>
      <div className="instance-actions">
        <button className="btn-sm btn-primary" onClick={handleSave}>Save</button>
        <button className="btn-sm" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function Settings() {
  const location = useLocation();
  const [accounts, setAccounts] = useState([]);
  const [strategyConfigs, setStrategyConfigs] = useState([]);
  const [strategyTypes, setStrategyTypes] = useState([]);
  const [strategyMeta, setStrategyMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingAccount, setEditingAccount] = useState(null);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ api_key: '', client_id: '', password: '', mpin: '', totp_secret: '', paper_trading: true });
  const [useDefaultKey, setUseDefaultKey] = useState(true);
  const [defaultApiKey, setDefaultApiKey] = useState('');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);
  const [addingStrategyType, setAddingStrategyType] = useState(null);
  const [newInstance, setNewInstance] = useState({ start_time: '', exit_time: '', strike_mode: 'ATM', itm_points: 0, lots: 1, account_id: '', is_active: true });

  // Handle OAuth callback navigation state
  useEffect(() => {
    if (location.state?.authMessage) {
      if (location.state.authOk) {
        setConnectionResult({ status: 'ok', message: location.state.authMessage });
      } else {
        setError(location.state.authMessage);
      }
      window.history.replaceState({}, '', location.pathname);
    }
  }, [location]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accRes, cfgRes, keyRes] = await Promise.allSettled([fetchAccounts(), fetchStrategyConfigs(), getAngelOneDefaultKey()]);
      if (accRes.status === 'fulfilled') setAccounts(accRes.value.data?.accounts || []);
      if (cfgRes.status === 'fulfilled') {
        setStrategyConfigs(cfgRes.value.data?.configs || []);
        setStrategyTypes(cfgRes.value.data?.strategy_types || []);
        setStrategyMeta(cfgRes.value.data?.strategy_meta || {});
      }
      if (keyRes.status === 'fulfilled' && keyRes.value.data?.api_key) setDefaultApiKey(keyRes.value.data.api_key);
    } catch (err) {
      setError('Failed to load settings');
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const handleSaveAccount = async () => {
    try {
      if (editingAccount) {
        await updateAccount(editingAccount.id, editingAccount);
        setEditingAccount(null);
      } else {
        const name = connectionResult?.profile?.name || newAccount.client_id;
        const payload = { ...newAccount, name };
        if (useDefaultKey && defaultApiKey) payload.api_key = defaultApiKey;
        await createAccount(payload);
        setNewAccount({ api_key: '', client_id: '', password: '', mpin: '', totp_secret: '', paper_trading: true });
        setShowAddAccount(false);
        setConnectionResult(null);
      }
      await loadSettings();
    } catch (err) {
      setError('Failed to save account');
    }
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    setConnectionResult(null);
    try {
      const payload = { ...newAccount };
      if (useDefaultKey && defaultApiKey) payload.api_key = defaultApiKey;
      const res = await testAccountConnection(payload);
      setConnectionResult(res.data);
    } catch (err) {
      setConnectionResult({ status: 'error', message: 'Connection failed. Check your credentials.' });
    }
    setTestingConnection(false);
  };

  const handleAngelOneLogin = async () => {
    try {
      const res = await getAngelOneLoginUrl();
      if (res.data?.status === 'ok' && res.data?.login_url) window.location.href = res.data.login_url;
      else setError(res.data?.message || 'Could not get login URL');
    } catch (err) { setError('Failed to initiate AngelOne login'); }
  };

  const handleKiteLogin = async () => {
    try {
      const res = await getKiteLoginUrl();
      if (res.data?.status === 'ok' && res.data?.login_url) window.location.href = res.data.login_url;
      else setError(res.data?.message || 'Could not get Kite login URL');
    } catch (err) { setError('Failed to initiate Kite login'); }
  };

  const handleSetDataFeed = async (id) => {
    try { await setDataFeedAccount(id); await loadSettings(); } catch { setError('Failed to set data feed account'); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm('Delete this broker account?')) return;
    try { await deleteAccount(id); await loadSettings(); } catch { setError('Failed to delete account'); }
  };

  const handleUpdateInstance = async (id, updates) => {
    try { await updateStrategyConfig(id, updates); await loadSettings(); } catch { setError('Failed to update strategy config'); }
  };

  const handleDeleteInstance = async (id) => {
    if (!window.confirm('Delete this strategy instance?')) return;
    try { await deleteStrategyConfig(id); await loadSettings(); } catch { setError('Failed to delete strategy instance'); }
  };

  const handleAddInstance = async (strategyType) => {
    try {
      await createStrategyConfig({
        strategy_name: strategyType,
        ...newInstance,
        account_id: newInstance.account_id || null,
      });
      setAddingStrategyType(null);
      setNewInstance({ start_time: '', exit_time: '', strike_mode: 'ATM', itm_points: 0, lots: 1, account_id: '', is_active: true });
      await loadSettings();
    } catch { setError('Failed to add strategy instance'); }
  };

  return (
    <div className="page-settings">
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Manage broker accounts and strategy configurations</p>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {loading && <div className="loading"><div className="spinner" /><p>Loading settings...</p></div>}

      {/* Broker Accounts Section */}
      <section className="settings-section">
        <div className="settings-section-header">
          <h2>Broker Accounts</h2>
          <button className="fetch-btn" onClick={() => setShowAddAccount(!showAddAccount)}>
            {showAddAccount ? 'Cancel' : '+ Add Account'}
          </button>
        </div>

        {showAddAccount && (
          <div className="account-form">
            <h3>Connect Broker Account</h3>
            {!connectionResult || connectionResult.status !== 'ok' ? (
              <>
                <div className="oauth-login-section">
                  <p className="settings-hint">Login securely via your broker — no credentials stored in this app.</p>
                  <div className="oauth-buttons">
                    <button className="fetch-btn btn-oauth btn-angelone" onClick={handleAngelOneLogin}>Login with AngelOne</button>
                    <button className="fetch-btn btn-oauth btn-kite" onClick={handleKiteLogin}>Login with Kite (Zerodha)</button>
                  </div>
                </div>
                <div className="login-divider"><span>or connect AngelOne manually</span></div>
                <p className="settings-hint">Enter your AngelOne account credentials.</p>
                <div className="api-key-section">
                  <label className="api-key-toggle">
                    <input type="checkbox" checked={useDefaultKey} onChange={e => setUseDefaultKey(e.target.checked)} />
                    {' '}Use shared API key {defaultApiKey ? <span className="api-key-preview">({defaultApiKey.slice(0, 4)}...)</span> : ''}
                  </label>
                  {!useDefaultKey && (
                    <div className="form-field" style={{ marginTop: 8 }}>
                      <label>Custom API Key</label>
                      <input type="text" value={newAccount.api_key} onChange={e => setNewAccount({...newAccount, api_key: e.target.value})} placeholder="Your own SmartAPI key" />
                    </div>
                  )}
                </div>
                <div className="form-grid">
                  <div className="form-field">
                    <label>Client ID</label>
                    <input type="text" value={newAccount.client_id} onChange={e => setNewAccount({...newAccount, client_id: e.target.value})} placeholder="e.g. D12345" />
                  </div>
                  <div className="form-field">
                    <label>Password / MPIN</label>
                    <input type="password" value={newAccount.password} onChange={e => setNewAccount({...newAccount, password: e.target.value})} placeholder="Trading password" />
                  </div>
                  <div className="form-field">
                    <label>TOTP Secret</label>
                    <input type="password" value={newAccount.totp_secret} onChange={e => setNewAccount({...newAccount, totp_secret: e.target.value})} placeholder="Base32 secret" />
                  </div>
                </div>
                <div className="account-form-actions">
                  <button className="fetch-btn btn-connect" onClick={handleTestConnection} disabled={testingConnection || (!useDefaultKey && !newAccount.api_key) || !newAccount.client_id || !newAccount.password || !newAccount.totp_secret}>
                    {testingConnection ? 'Connecting...' : 'Connect to AngelOne'}
                  </button>
                </div>
                {connectionResult && connectionResult.status === 'error' && (
                  <div className="connection-result connection-error">{connectionResult.message}</div>
                )}
              </>
            ) : connectionResult.profile ? (
              <>
                <div className="profile-card">
                  <div className="profile-card-header">
                    <div>
                      <h4>{connectionResult.profile.name || connectionResult.profile.client_id}</h4>
                      <span className="profile-client-id">{connectionResult.profile.client_id}</span>
                    </div>
                    <span className="connection-badge-ok">Connected</span>
                  </div>
                  <div className="profile-details">
                    {connectionResult.profile.email && (
                      <div className="profile-detail-item"><span className="profile-label">Email</span><span>{connectionResult.profile.email}</span></div>
                    )}
                    <div className="profile-detail-item">
                      <span className="profile-label">Available Funds</span>
                      <span className="profile-funds">₹{connectionResult.funds?.available_cash !== 'N/A' ? Number(connectionResult.funds.available_cash).toLocaleString('en-IN') : 'N/A'}</span>
                    </div>
                  </div>
                  <div className="profile-card-footer">
                    <label><input type="checkbox" checked={newAccount.paper_trading} onChange={e => setNewAccount({...newAccount, paper_trading: e.target.checked})} /> Paper Trading Mode</label>
                  </div>
                </div>
                <div className="account-form-actions">
                  <button className="fetch-btn btn-connect" onClick={handleSaveAccount}>Save Account</button>
                  <button className="fetch-btn" onClick={() => setConnectionResult(null)}>Back</button>
                </div>
              </>
            ) : (
              <>
                <div className="connection-result connection-success"><p>{connectionResult.message}</p></div>
                <div className="account-form-actions">
                  <button className="fetch-btn" onClick={() => { setConnectionResult(null); setShowAddAccount(false); }}>Done</button>
                </div>
              </>
            )}
          </div>
        )}

        {accounts.length > 0 && (
          <div className="table-wrapper">
            <table className="strat-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Broker</th>
                  <th>Client ID</th>
                  <th>Mode</th>
                  <th>Auth</th>
                  <th>Connection</th>
                  <th>Data Feed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map(acc => (
                  <tr key={acc.id}>
                    <td>{editingAccount?.id === acc.id
                      ? <input type="text" value={editingAccount.name} onChange={e => setEditingAccount({...editingAccount, name: e.target.value})} />
                      : acc.name}</td>
                    <td><span className={`broker-badge broker-${acc.broker || 'angelone'}`}>{acc.broker === 'kite' ? 'Kite' : 'AngelOne'}</span></td>
                    <td>{acc.client_id}</td>
                    <td>{acc.paper_trading ? 'Paper' : 'Live'}</td>
                    <td>{acc.login_method === 'oauth' ? 'OAuth' : 'Manual'}</td>
                    <td>
                      <span className={`connection-dot ${acc.is_connected ? 'connected' : 'disconnected'}`}>
                        {acc.is_connected ? '● Connected' : '○ Disconnected'}
                      </span>
                    </td>
                    <td>
                      {acc.is_data_feed ? (
                        <span className="data-feed-badge">Active</span>
                      ) : (
                        <button className="btn-sm" onClick={() => handleSetDataFeed(acc.id)}>Set as Data Feed</button>
                      )}
                    </td>
                    <td>
                      {editingAccount?.id === acc.id ? (
                        <>
                          <button className="btn-sm" onClick={handleSaveAccount}>Save</button>
                          <button className="btn-sm" onClick={() => setEditingAccount(null)}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button className="btn-sm" onClick={() => setEditingAccount({...acc})}>Edit</button>
                          <button className="btn-sm btn-danger" onClick={() => handleDeleteAccount(acc.id)}>Delete</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && accounts.length === 0 && (<p className="empty-msg">No broker accounts configured.</p>)}
      </section>

      {/* Strategy Instances Section */}
      <section className="settings-section">
        <h2>Strategy Instances</h2>
        <p className="settings-hint">Each strategy can run on multiple accounts with different configurations.</p>

        {strategyTypes.map(stype => {
          const instances = strategyConfigs.filter(c => c.strategy_name === stype);
          const meta = strategyMeta[stype] || {};
          return (
            <div key={stype} className="strategy-type-card">
              <div className="strategy-type-header">
                <div>
                  <h3>{meta.display_name || stype}</h3>
                  {meta.description && <p className="strategy-type-desc">{meta.description}</p>}
                  <span className="strategy-type-meta">{meta.index} | {meta.trading_day} | {meta.default_start_time}–{meta.default_exit_time} | {meta.adjustment_points}pt adj</span>
                </div>
                <div className="strategy-type-actions">
                  <span className="instance-count">{instances.filter(i => i.is_active).length} active</span>
                  <button className="btn-sm btn-primary" onClick={() => {
                    if (addingStrategyType === stype) {
                      setAddingStrategyType(null);
                    } else {
                      setAddingStrategyType(stype);
                      setNewInstance({
                        start_time: meta.default_start_time || '09:20',
                        exit_time: meta.default_exit_time || '14:40',
                        strike_mode: 'ATM', itm_points: 0, lots: 1, account_id: '', is_active: true,
                      });
                    }
                  }}>
                    {addingStrategyType === stype ? 'Cancel' : '+ Add Instance'}
                  </button>
                </div>
              </div>

              {addingStrategyType === stype && (
                <div className="instance-row instance-editing">
                  <div className="instance-form-grid">
                    <div className="form-field">
                      <label>Account</label>
                      <select value={newInstance.account_id} onChange={e => setNewInstance({...newInstance, account_id: e.target.value ? parseInt(e.target.value) : ''})}>
                        <option value="">Paper Trading</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Start Time</label>
                      <input type="time" value={newInstance.start_time} onChange={e => setNewInstance({...newInstance, start_time: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <label>Exit Time</label>
                      <input type="time" value={newInstance.exit_time} onChange={e => setNewInstance({...newInstance, exit_time: e.target.value})} />
                    </div>
                    <div className="form-field">
                      <label>Strike Mode</label>
                      <select value={newInstance.strike_mode} onChange={e => setNewInstance({...newInstance, strike_mode: e.target.value})}>
                        <option value="ATM">ATM</option>
                        <option value="ITM">ITM</option>
                      </select>
                    </div>
                    {newInstance.strike_mode === 'ITM' && (
                      <div className="form-field">
                        <label>ITM Points</label>
                        <input type="number" value={newInstance.itm_points} onChange={e => setNewInstance({...newInstance, itm_points: parseInt(e.target.value) || 0})} min="0" step="50" />
                      </div>
                    )}
                    <div className="form-field">
                      <label>Lots</label>
                      <input type="number" value={newInstance.lots} onChange={e => setNewInstance({...newInstance, lots: parseInt(e.target.value) || 1})} min="1" />
                    </div>
                  </div>
                  <div className="instance-actions">
                    <button className="btn-sm btn-primary" onClick={() => handleAddInstance(stype)}>Create</button>
                    <button className="btn-sm" onClick={() => setAddingStrategyType(null)}>Cancel</button>
                  </div>
                </div>
              )}

              {instances.length === 0 && !addingStrategyType && (<p className="empty-msg">No instances configured.</p>)}
              {instances.map(inst => (
                <StrategyInstance key={inst.id} instance={inst} accounts={accounts} meta={meta} onSave={handleUpdateInstance} onDelete={handleDeleteInstance} />
              ))}
            </div>
          );
        })}
      </section>
    </div>
  );
}
